import React from 'react';
import { AppLink } from '~/components/AppLink';

import { Search, MapPin, Calendar, X } from 'lucide-react';
import { Card } from '@agentselly/ui';

import { AppRoute } from '@agentselly/immodossier-shared';
import { useAssetConfig } from '../../context/AssetConfigContext';
import { useAuthz } from '../../context/AuthzContext';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/formatCurrency';

export interface PropertyListItem {
  id: string;
  title: string;
  status?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  photoUrl?: string | null;
  address?: {
    street?: string;
    no?: string;
    zip?: string;
    city?: string;
  };
  numberOfRooms?: number;
  livingArea?: number;
  buildingYear?: number;
  category?: string;
  price?: number;
  upcomingAppointmentsCount?: number;
  lead?: { type?: string; hubspotContactId?: string };
  leadStatus?: string;
  isDossier?: boolean;
  section?: string;
  sectionTitle?: string;
  hubdbExternalAgentId?: string;
  hubdbInternalAgentId?: string;
  hubspotContactId?: string;
  // Extended fields from MongoDB
  estimatedValue?: number;
  askingPriceSeller?: number;
  buildYear?: number;
  trustScore?: {
    trustScore: number;
    trustScoreExplain?: {
      checkAddress?: number;
      checkPhone?: number;
      checkName?: number;
      checkIPRegistry?: number;
      checkUserAgent?: number;
      checkZerobounce?: number;
    };
  };
  evaluation?: {
    estimatedValue?: number;
    estimatedLowerValue?: number;
    estimatedUpperValue?: number;
    timestamp?: string;
    sources?: {
      iazi?: {
        estimatedValue?: number;
        estimatedLowerValue?: number;
        estimatedUpperValue?: number;
        qualityText?: string;
        date?: string;
      };
      priceHubble?: {
        estimatedValue?: number;
      };
      realmatch?: {
        estimatedValue?: number;
      };
    };
  };
  priceKnown?: boolean;
  bathrooms?: number;
  hasLift?: boolean | string;
  hasBalcony?: boolean;
  hasGarage?: boolean;
  parcelSpace?: number;
  honors?: {
    type?: string;
    value?: number;
  };
  consultantId?: string;
  consultantAssignedBy?: string;
}

interface PropertyListTableProps {
  properties: PropertyListItem[];
  params: any;
  mode?: 'in_care' | 'cockpit';
}

const STATUS_CONFIG_STATIC = {
  // Lead Types (from Lead.type)
  sellerlead: {
    label: 'Verkaufsinteresse',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    dot: 'bg-blue-500',
    order: 12,
  },
  successful: {
    label: 'Erfolgreich',
    color: 'text-green-700',
    bg: 'bg-green-50',
    dot: 'bg-green-500',
    order: 6,
  },
  online: {
    label: 'Online',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
    order: 2,
  },
  available: {
    label: 'Online',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
    order: 2,
  },
  sold: {
    label: 'Erfolgreich',
    color: 'text-green-700',
    bg: 'bg-green-50',
    dot: 'bg-green-500',
    order: 6,
  },
  presale: {
    label: 'Vorverkauf',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    dot: 'bg-amber-500',
    order: 4,
  },
  reserved: {
    label: 'Reserviert',
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    dot: 'bg-purple-500',
    order: 2,
  },
  recorded: {
    label: 'Erfasst',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    dot: 'bg-indigo-500',
    order: 3,
  },
  notsold: {
    label: 'Nicht verkauft',
    color: 'text-red-700',
    bg: 'bg-red-50',
    dot: 'bg-red-500',
    order: 7,
  },
  cancel: {
    label: 'Abgebrochen',
    color: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-800',
    dot: 'bg-gray-400 dark:bg-gray-500',
    order: 8,
  },
  completed: {
    label: 'Abgeschlossen',
    color: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-800',
    dot: 'bg-gray-400 dark:bg-gray-500',
    order: 9,
  },
  lessthan2months: {
    label: 'Weniger als 2 Monate',
    color: 'text-orange-700',
    bg: 'bg-orange-50',
    dot: 'bg-orange-500',
    order: 1,
  },
  delay: {
    label: 'Verzögert',
    color: 'text-yellow-700',
    bg: 'bg-yellow-50',
    dot: 'bg-yellow-500',
    order: 5,
  },

  // Lead Status values (common ones from Lead.status)
  notest: {
    label: 'Kein Test',
    color: 'text-slate-700 dark:text-slate-300',
    bg: 'bg-slate-50 dark:bg-slate-800',
    dot: 'bg-slate-500',
    order: 20,
  },
  nosale: {
    label: 'Kein Verkauf',
    color: 'text-red-700',
    bg: 'bg-red-50',
    dot: 'bg-red-500',
    order: 21,
  },
  bsi: {
    label: 'BSI',
    color: 'text-cyan-700',
    bg: 'bg-cyan-50',
    dot: 'bg-cyan-500',
    order: 22,
  },
  contractsent: {
    label: 'Vertrag gesendet',
    color: 'text-teal-700',
    bg: 'bg-teal-50',
    dot: 'bg-teal-500',
    order: 23,
  },
  contractreceived: {
    label: 'Vertrag erhalten',
    color: 'text-green-700',
    bg: 'bg-green-50',
    dot: 'bg-green-500',
    order: 24,
  },
  fresh: {
    label: 'Neu',
    color: 'text-lime-700',
    bg: 'bg-lime-50',
    dot: 'bg-lime-500',
    order: 25,
  },
  new: {
    label: 'Neu',
    color: 'text-lime-700',
    bg: 'bg-lime-50',
    dot: 'bg-lime-500',
    order: 25,
  },
} as const;

export function getStatusConfigStatic(status?: string) {
  if (!status || status === '') {
    return null;
  }

  const key = status.toLowerCase() as keyof typeof STATUS_CONFIG_STATIC;
  const found = STATUS_CONFIG_STATIC[key];

  if (found) {
    return found;
  }

  return null;
}
function getStatusOrder(status?: string): number {
  if (!status || status === '') return 999;

  const key = status.toLowerCase() as keyof typeof STATUS_CONFIG_STATIC;
  const found = STATUS_CONFIG_STATIC[key];

  if (found) {
    return found.order;
  }

  return 999;
}
const SECTION_CONFIG = {
  in_betreuung: { order: 1, defaultExpanded: true },
  ihre_objekte: { order: 2, defaultExpanded: false },
  cockpit_objekte: { order: 3, defaultExpanded: false },
  dossiers: { order: 4, defaultExpanded: false },
} as const;

function formatCategory(c?: string): string {
  if (!c) return '';
  const s = String(c);
  if (s.includes('$')) return '';
  switch (s.toLowerCase()) {
    case 'house':
      return 'Haus';
    case 'flat':
      return 'Wohnung';
    default:
      return s;
  }
}

const NO_IMAGE_PLACEHOLDER =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="80"%3E%3Crect fill="%23f3f4f6" width="120" height="80"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="10" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EKein Bild%3C/text%3E%3C/svg%3E';

function getPropertyImageUrl(
  property: PropertyListItem,
  assetServerUrl?: string,
): string {
  if (property.photoUrl) {
    if (
      property.photoUrl.startsWith('http://') ||
      property.photoUrl.startsWith('https://')
    ) {
      return property.photoUrl;
    }
    if (assetServerUrl) {
      return `${assetServerUrl.replace(/\/$/, '')}${property.photoUrl}`;
    }

    return `https://assets.agentselly.${import.meta.env.PROD ? 'ch' : 'dev'}${
      property.photoUrl
    }`;
  }

  const address = property.address;
  if (address?.street && address?.city) {
    const location = `${address.street} ${address.no || ''} ,${
      address.zip || ''
    } ${address.city}`.trim();
    const encodedLocation = encodeURIComponent(location);
    return `https://property-cover.selly.ch/location/${encodedLocation}`;
  }

  return NO_IMAGE_PLACEHOLDER;
}

// Property Row Component - Clickable Link
function PropertyRow({
  property,
  assetServerUrl,
  params,
}: {
  readonly property: PropertyListItem;
  readonly assetServerUrl?: string;
  readonly mode?: 'in_care' | 'cockpit';
  readonly params: any;
}) {
  const { atLeast } = useAuthz();
  const showPriceAndValuation = atLeast('partner');
  const category = formatCategory(property.category);
  const statusConfigStatic = getStatusConfigStatic(property.status);
  const statusLabel = statusConfigStatic?.label;
  const imageUrl = getPropertyImageUrl(property, assetServerUrl);
  const addressTitle = property.address
    ? `${property.address.street || ''} ${property.address.no || ''}, ${
        property.address.zip || ''
      } ${property.address.city || ''}`.trim()
    : '';
  const hasDistinctTitle =
    property.title.trim().replace(/\s+/g, ' ') !==
    addressTitle.replace(/\s+/g, ' ');

  // Customer data from property lead info
  const customerData = property.lead
    ? {
        name: property.lead.name || 'Unbekannt',
        email: property.lead.email || 'N/A',
        phone: property.lead.phone || 'N/A',
      }
    : {
        name: 'Keine Kundendaten',
        email: 'N/A',
        phone: 'N/A',
      };

  const commonContent = (
    <>
      {/* Photo Thumbnail - Large matching content height */}
      <div className="aspect-[3/2] w-full overflow-hidden bg-muted sm:h-20 sm:w-20 sm:aspect-auto sm:flex-shrink-0 sm:rounded-md">
        <img
          src={imageUrl}
          alt={property.title}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.src = NO_IMAGE_PLACEHOLDER;
          }}
        />
      </div>

      {/* Content */}
      <div className="w-full min-w-0 px-4 sm:w-auto sm:flex-1 sm:px-0">
        {hasDistinctTitle && (
          <div className="mb-2 flex flex-wrap items-start gap-2">
            <h3 className="font-medium text-sm text-foreground truncate">
              {property.title}
            </h3>
          </div>
        )}

        {(property.upcomingAppointmentsCount ?? 0) > 0 && (
          <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
            <AppLink
              to="/$lang/$companyId/$contactId/cockpit/$propertyId/calendar"
              params={{
                lang: params.lang,
                companyId: params.companyId,
                contactId: params.contactId,
                propertyId: property.id,
              }}
              className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
            >
              <Calendar className="h-3 w-3" />
              <span>
                {property.upcomingAppointmentsCount}{' '}
                {property.upcomingAppointmentsCount === 1
                  ? 'Termin'
                  : 'Termine'}
              </span>
            </AppLink>
          </div>
        )}

        {/* Address */}
        {property.address && (
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">
              {property.address.street} {property.address.no},{' '}
              {property.address.zip} {property.address.city}
            </span>
          </div>
        )}

        {/* Property Details */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {property.numberOfRooms != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Zimmer</dt>
              <dd className="font-medium text-foreground tabular-nums">
                {property.numberOfRooms}
              </dd>
            </div>
          )}
          {property.livingArea != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Fläche</dt>
              <dd className="font-medium text-foreground tabular-nums">
                {property.livingArea} m²
              </dd>
            </div>
          )}
          {category && (
            <div>
              <dt className="text-xs text-muted-foreground">Typ</dt>
              <dd className="font-medium text-foreground tabular-nums">
                {category}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Customer Details - Right Column - Only for partners */}
      {showPriceAndValuation && customerData.name !== 'Keine Kundendaten' && (
        <div className="w-full min-w-0 px-4 py-2 sm:w-52 sm:flex-shrink-0 sm:px-3">
          <div className="mb-0.5 flex items-center">
            <span className="text-sm font-medium text-foreground truncate">
              {customerData.name}
            </span>
          </div>
          <div className="space-y-2 text-sm">
            {customerData.email !== 'N/A' && (
              <div className="flex items-center">
                {/* Asymmetric padding + negative margin: 20px line + 24px padding = 44px
                    touch target while the visible row stays one line high. Email expands
                    upward (over the non-interactive name), phone downward, so the two
                    hit areas meet at the 8px gap midpoint without overlapping. */}
                <a
                  href={`mailto:${customerData.email}`}
                  className="inline-flex min-w-0 items-center pt-5 pb-1 -mt-5 -mb-1 text-blue-600 hover:underline truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {customerData.email}
                </a>
              </div>
            )}
            {customerData.phone !== 'N/A' && (
              <div className="flex items-center">
                <a
                  href={`tel:${customerData.phone}`}
                  className="inline-flex items-center pt-1 pb-5 -mt-1 -mb-5 text-blue-600 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {customerData.phone}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right Side - Status & Price */}
      <div className="flex w-full flex-row flex-wrap items-center justify-between gap-2 px-4 pb-4 sm:w-auto sm:flex-col sm:items-end sm:justify-start sm:flex-shrink-0 sm:px-0 sm:pb-0">
        {property.status && statusConfigStatic && (
          <div
            className={cn(
              'inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium',
              statusConfigStatic.bg,
              statusConfigStatic.color,
            )}
          >
            {statusLabel}
          </div>
        )}

        {/* Preis - Nur für AB Partner */}
        {showPriceAndValuation && property.price != null && (
          <div>
            <span className="whitespace-nowrap text-lg font-semibold text-foreground tabular-nums">
              {formatCurrency(Number(property.price))}
            </span>
          </div>
        )}
      </div>
    </>
  );

  // Render as Link für Properties
  if (!property.isDossier) {
    return (
      <AppLink<any>
        to={AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME}
        params={{ ...params, propertyId: property.id } as any}
        className="group mx-4 my-3 block overflow-hidden rounded-lg bg-card shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-colors hover:bg-muted/50 sm:mx-0 sm:my-0 sm:rounded-none sm:border-b sm:border-border sm:p-5 sm:shadow-none sm:last:border-b-0"
      >
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4">
          {commonContent}
        </div>
      </AppLink>
    );
  }

  // Render als div für Dossiers (nicht klickbar)
  return (
    <div className="mx-4 my-3 flex flex-col items-start gap-3 overflow-hidden rounded-lg bg-card shadow-[0_1px_3px_rgba(0,0,0,0.12)] sm:mx-0 sm:my-0 sm:flex-row sm:rounded-none sm:border-b sm:border-border sm:bg-muted/30 sm:p-4 sm:shadow-none sm:last:border-b-0">
      {commonContent}
    </div>
  );
}

// Status Group Component mit besserer UI
function StatusGroup({
  status,
  sectionTitle,
  properties,
  assetServerUrl,
  mode,
  params,
  isExpanded,
  onToggle,
}: {
  readonly status: string;
  readonly section: string;
  readonly sectionTitle?: string;
  readonly leadType?: string;
  readonly properties: PropertyListItem[];
  readonly params: any;
  readonly assetServerUrl?: string;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly mode?: 'in_care' | 'cockpit';
  onPropertySelect?: (property: PropertyListItem) => void;
}) {
  const statusConfigStatic = getStatusConfigStatic(status);
  const groupLabel = statusConfigStatic?.label || sectionTitle || 'Immobilien';
  const groupDot = statusConfigStatic?.dot || 'bg-muted-foreground';
  const groupBg = statusConfigStatic?.bg || 'bg-muted';
  const groupColor = statusConfigStatic?.color || 'text-muted-foreground';

  return (
    <div className="border-b border-border/50 last:border-b-0">
      {/* Group Header - Clickable */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-border/20 bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className={cn('h-3 w-3 rounded-full', groupDot)} />
          <h3 className="font-semibold text-foreground">{groupLabel}</h3>
          <span
            className={cn(
              'inline-flex items-center justify-center rounded-full text-xs font-medium',
              'min-w-[1.25rem] h-5 px-1.5 py-0.5',
              'bg-white dark:bg-gray-800/90 dark:bg-gray-900/90 hover:bg-white dark:hover:bg-gray-800 dark:bg-gray-800/95 dark:hover:bg-gray-900/95 transition-all duration-150',
              groupBg,
              groupColor,
              groupColor.replace('text-', 'border-'),
              'border border-white/20',
              'text-[10px]',
            )}
          >
            {properties.length >= 100 ? '99+' : properties.length}
          </span>
        </div>
        <div className="text-muted-foreground">
          <svg
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              isExpanded ? 'rotate-180' : 'rotate-0',
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Group Items - Only show when expanded */}
      {isExpanded && (
        <div className="bg-background">
          {properties.map((property) => (
            <PropertyRow
              key={property.id}
              property={property}
              assetServerUrl={assetServerUrl}
              mode={mode}
              params={params}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function PropertyListTable({
  properties,
  params,
  mode = 'cockpit',
}: PropertyListTableProps) {
  const assetConfig = useAssetConfig();
  const [
    searchQuery,
    setSearchQuery,
  ] = React.useState('');
  const [
    expandedGroups,
    setExpandedGroups,
  ] = React.useState<Set<string>>(new Set());

  // Filter properties based on search
  const filteredProperties = React.useMemo(() => {
    if (!searchQuery) return properties;

    const query = searchQuery.toLowerCase();
    return properties.filter((property) => {
      const searchableText = [
        property.title,
        property.id,
        property.address?.street,
        property.address?.city,
        property.address?.zip,
        property.category,
        property.status,
        property.leadStatus,
        property.lead?.type,
        property.sectionTitle,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [
    properties,
    searchQuery,
  ]);

  const groupedProperties = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        section: string;
        status: string;
        items: PropertyListItem[];
        sectionTitle?: string;
        leadType?: string;
      }
    >();

    filteredProperties.forEach((property) => {
      let groupKey: string;
      let leadType: string | undefined;
      let sectionTitle: string | undefined;

      if (mode === 'in_care') {
        leadType = property.isDossier ? '' : property.lead?.type;
        const status =
          property.lead?.type?.toLowerCase() ||
          property.status?.toLowerCase() ||
          property.leadStatus?.toLowerCase() ||
          '';
        groupKey = `${leadType}|${status}`;
        sectionTitle = undefined;

        if (!groups.has(groupKey)) {
          groups.set(groupKey, {
            section: 'betreuung',
            status,
            items: [],
            sectionTitle,
            leadType,
          });
        }
      } else {
        const section = property.section || 'cockpit';
        const status =
          property.lead?.type?.toLowerCase() ||
          property.status?.toLowerCase() ||
          property.leadStatus?.toLowerCase() ||
          '';
        groupKey = `${section}|${status}`;
        sectionTitle = property.sectionTitle;

        if (!groups.has(groupKey)) {
          groups.set(groupKey, {
            section,
            status,
            items: [],
            sectionTitle,
          });
        }
      }

      const group = groups.get(groupKey);
      if (group) {
        group.items.push(property);
      }
    });

    return groups;
  }, [
    filteredProperties,
    mode,
  ]);

  React.useEffect(() => {
    if (groupedProperties.size > 0) {
      if (searchQuery) {
        const allGroupKeys = Array.from(groupedProperties.keys());
        setExpandedGroups(new Set(allGroupKeys));
      } else if (expandedGroups.size === 0) {
        // Auto-expand "online" and "lessthan2months" status groups
        const statusGroupsToExpand = new Set<string>();

        groupedProperties.forEach((group, key) => {
          const status = group.status?.toLowerCase();
          if (status === 'online' || status === 'lessthan2months') {
            statusGroupsToExpand.add(key);
          }
        });

        // If no online/lessthan2months found, expand the first group
        if (statusGroupsToExpand.size === 0) {
          const sortedGroups = Array.from(groupedProperties.entries()).sort(
            ([keyA], [keyB]) => {
              const [sectionA] = keyA.split('|');
              const [sectionB] = keyB.split('|');

              const sectionOrderA =
                SECTION_CONFIG[sectionA as keyof typeof SECTION_CONFIG]
                  ?.order || 999;
              const sectionOrderB =
                SECTION_CONFIG[sectionB as keyof typeof SECTION_CONFIG]
                  ?.order || 999;

              return sectionOrderA - sectionOrderB;
            },
          );

          const firstGroupKey = sortedGroups[0][0];
          statusGroupsToExpand.add(firstGroupKey);
        }

        setExpandedGroups(statusGroupsToExpand);
      }
    }
  }, [
    groupedProperties,
    searchQuery,
  ]);

  const toggleGroup = React.useCallback(
    (key: string) => {
      const newExpanded = new Set(expandedGroups);
      if (newExpanded.has(key)) {
        newExpanded.delete(key);
      } else {
        newExpanded.add(key);
      }
      setExpandedGroups(newExpanded);
    },
    [expandedGroups],
  );

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex items-center justify-between mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Immobilien durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Empty State */}
      {filteredProperties.length === 0 ? (
        <Card className="p-8 text-center">
          <Search className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium text-foreground">
            Keine Immobilien gefunden
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {searchQuery
              ? 'Versuchen Sie andere Suchbegriffe'
              : 'Momentan keine Immobilien vorhanden'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {Array.from(groupedProperties.entries())
            .sort(([keyA], [keyB]) => {
              const [
                sectionA,
                statusA,
              ] = keyA.split('|');
              const [
                sectionB,
                statusB,
              ] = keyB.split('|');

              const sectionOrderA =
                SECTION_CONFIG[sectionA as keyof typeof SECTION_CONFIG]
                  ?.order || 999;
              const sectionOrderB =
                SECTION_CONFIG[sectionB as keyof typeof SECTION_CONFIG]
                  ?.order || 999;
              const statusOrderA = getStatusOrder(statusA);
              const statusOrderB = getStatusOrder(statusB);

              // First sort by section order, then by status order
              if (sectionOrderA !== sectionOrderB) {
                return sectionOrderA - sectionOrderB;
              }
              return statusOrderA - statusOrderB;
            })
            .map(
              ([
                groupKey,
                groupData,
              ]) => {
                const handleToggle = () => toggleGroup(groupKey);

                return (
                  <StatusGroup
                    key={groupKey}
                    status={groupData.status}
                    section={groupData.section}
                    sectionTitle={groupData.sectionTitle}
                    leadType={groupData.leadType}
                    properties={groupData.items}
                    params={params}
                    assetServerUrl={assetConfig.publicAssetServerUrl}
                    isExpanded={expandedGroups.has(groupKey)}
                    onToggle={handleToggle}
                    mode={mode}
                  />
                );
              },
            )}
        </Card>
      )}
    </div>
  );
}
